import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Link,
  Modal,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CustomCheckBox } from "../../v2/utils/CustomCheckBox";
import React, { useEffect, useMemo, useRef, useState } from "react";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import { makeStyles } from "@mui/styles";
import { Role } from "../../../enums";
import { CustomChip } from "../../v2/utils/CustomChip";
import axios from "../../../services/axios";
import { DbUser } from "../../../interfaces";
import { generateGtmEvent } from "../../../Helpers";

const useStyles = makeStyles((theme) => ({
  filterModal: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
}));

interface UserFilterModal {
  currentUser: DbUser;
  open: boolean;
  handleClose: () => void;
  currentTab: number;
  applyFilter: (data: any) => void;
  activeFilters?: any;
}

let AVAILABLE_ROLES = {
  Student: Role.student,
  Admin: Role.admin,
  Teacher: Role.teacher,
  "Super admin": Role.super,
};

interface MultiSelectInputProps {
  label: string;
  gtmType?: string;
  gtmSection?: string;
  optionList: any[];
  selectedItems: any[];
  setSelectedItems: (selectedItems: any[]) => void;
}

const MultiSelectInputField = (props: MultiSelectInputProps) => {
  const { label, optionList, selectedItems, setSelectedItems, gtmType, gtmSection } =
    props;
  let textInputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <Typography
        component={"p"}
        variant="h6"
        sx={{ color: "#000000", fontWeight: "bold", fontSize: "24px" }}>
        {label}
      </Typography>
      <Autocomplete
        disableClearable={true}
        ref={textInputRef}
        value={selectedItems}
        openOnFocus={true}
        noOptionsText={"No results found"}
        onChange={(e, value) => {
          console.log("dataLayerObj", value);
          generateGtmEvent({
            event: "select",
            action: "click",
            name: "onclick",
            type: gtmType,
            region: "main content",
            section: gtmSection,
            text: `${value.map((item) => (item.name ? item.name : item)).join(",")}`,
          });
          setSelectedItems(value);
        }}
        multiple
        id="tags-outlined"
        options={optionList}
        getOptionLabel={(option) => (option.name ? option.name : option)}
        renderInput={(params) => {
          return (
            <TextField
              sx={{
                "& .MuiAutocomplete-endAdornment": {
                  top: "calc(50% - 25px) !important",
                },
              }}
              {...params}
              label={`${label}`}
              onChange={(e) => {
                generateGtmEvent({
                  event: "form",
                  action: "click",
                  name: "onclick",
                  type: "blur",
                  region: "main content",
                  section: gtmSection,
                  text: e.target.value,
                });
              }}
              placeholder={`Select ${label}`}
              inputRef={(input) => {
                textInputRef.current = input;
              }}
            />
          );
        }}
        renderOption={(props, option, { selected }) => {
          return selected ? null : (
            <li {...props}>{option.name ? option.name : option}</li>
          );
        }}
        renderTags={(value, getTagProps) => {
          return value.map((option, index) => {
            return (
              <CustomChip
                showAvatar={false}
                key={`ob-chip-${index}`}
                label={option.name ? option.name : option}
                onClose={(e) => {
                  generateGtmEvent({
                    event: "select",
                    action: "unclick",
                    name: "onclick",
                    type: "checkbox",
                    region: "main content",
                    section: gtmSection,
                    text: option.name ? option.name : option,
                  });
                  getTagProps({ index }).onDelete(e);
                }}
              />
            );
          });
        }}
      />
    </>
  );
};

export const FilterModal = (props: UserFilterModal) => {
  const { open, handleClose, currentTab, applyFilter, activeFilters } = props;
  const classes = useStyles();
  const horiBreakpointSm = useMediaQuery("(max-width:769px)");
  const [schoolList, setSchoolList] = useState<any[]>([]);
  const [teacherList, setTeacherList] = useState<any[]>([]);
  const [assignmentList, setAssignmentList] = useState<any[]>([]);

  const [selectedTeacher, setSelectedTeacher] = useState<any[]>(
    activeFilters.teacher || []
  );
  const [selectedSchool, setSelectedSchool] = useState<any[]>(
    activeFilters.school_name || []
  );

  const [selectedSectionSLN, setSelectedSectionSLN] = useState<any[]>(
    activeFilters.section_sln || []
  );
  const [selectedSectionNumber, setSelectedSectionNumber] = useState<any[]>(
    activeFilters.canvas_section_number || []
  );
  const [selectedTerm, setSelectedTerm] = useState<any[]>(
    activeFilters.canvas_term_name || []
  );
  const [selectedCourse, setSelectedCourse] = useState<any[]>(
    activeFilters.course_name || []
  );

  const [selectedDemo, setSelectedDemo] = useState(activeFilters.role || false);

  const [selectedRole, setSelectedRole] = useState<any[]>(activeFilters.role || []);

  const ltiFilterOps = useMemo(() => {
    if (!assignmentList?.length) {
      return { courseOps: [], termOps: [], slnOps: [], sectionOps: [] };
    }

    const buildOps = (values: any[]) =>
      unique(values)
        .sort((a, b) => String(a).localeCompare(String(b)))
        .map((v) => ({ id: v, name: v }));

    return {
      courseOps: buildOps(assignmentList.map((a) => a.course_name)),
      termOps: buildOps(assignmentList.map((a) => a.canvas_term_name)),
      slnOps: buildOps(assignmentList.map((a) => a.section_sln)),
      sectionOps: buildOps(assignmentList.map((a) => a.canvas_section_number)),
    };
  }, [assignmentList]);

  useEffect(() => {
    if (currentTab === 7) {
      const fetchAssignments = async () => {
        try {
          let response = await axios.post(
            "/lti/1p3/assignments",
            {},
            {
              params: {
                disablePagination: true,
              },
            }
          );
          setAssignmentList(response.data.assignments);
        } catch (e) {
          console.log(e);
        }
      };
      fetchAssignments();
    }
    if (currentTab === 1 || currentTab === 0 || currentTab === 4) {
      const fetchSchools = async () => {
        try {
          let response = await axios.get(`/schools`);
          setSchoolList(response.data);
        } catch (e) {
          console.log(e);
        }
      };
      fetchSchools();
    }
    if (currentTab === 1) {
      const fetchTeachers = async () => {
        try {
          let response = await axios.get(`/teachers`);
          let data = response.data.map((teacher: any) => {
            return {
              id: teacher.id,
              name: teacher.full_name,
            };
          });
          setTeacherList(data);
        } catch (e) {
          console.log(e);
        }
      };
      fetchTeachers();
    }
  }, [currentTab]);

  useEffect(() => {
    setSelectedRole(activeFilters.role || []);
    setSelectedSchool(activeFilters.school_name || []);
    setSelectedTeacher(activeFilters.teacher || []);
    setSelectedDemo(activeFilters.is_demo || false);
    setSelectedSectionSLN(activeFilters.section_sln || []);
    setSelectedSectionNumber(activeFilters.canvas_section_number || []);
    setSelectedTerm(activeFilters.canvas_term_name || []);
    setSelectedCourse(activeFilters.course_name || []);
  }, [activeFilters]);

  const clearFilter = () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "filter",
      text: "clear all",
    });
    setSelectedRole([]);
    setSelectedSchool([]);
    setSelectedTeacher([]);
    setSelectedDemo(false);
    setSelectedSectionSLN([]);
    setSelectedSectionNumber([]);
    setSelectedTerm([]);
    setSelectedCourse([]);
    applyFilter({
      role: [],
      school_name: [],
      teacher: [],
      is_demo: false,
      section_sln: [],
      canvas_section_number: [],
      canvas_term_name: [],
      course_name: [],
    });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby={"user-managment-filter-modal"}>
        <Card
          sx={{ width: horiBreakpointSm ? "600px" : "900px" }}
          className={classes.filterModal}>
          <CardHeader
            sx={{ padding: "16px 24px" }}
            title={
              <Typography
                id="edit-account-modal-title"
                variant="h6"
                className={classes.modalTitle}>
                Filter
              </Typography>
            }
            action={
              <Tooltip
                placement="bottom-end"
                title="Close"
                arrow
                role="tooltip"
                describeChild>
                <IconButton
                  onClick={() => {
                    generateGtmEvent({
                      event: "modal",
                      action: "close",
                      name: "onclick",
                      type: "click",
                      region: "main content",
                      section: "filter",
                      text: "close cross",
                    });
                    handleClose();
                  }}
                  aria-label="close overall progress filter modal"
                  className={classes.closeButton}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent
            sx={{
              padding: "24px 32px",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}>
            {(currentTab === 0 || currentTab === 3) && (
              <>
                <Typography
                  component={"p"}
                  variant="h6"
                  sx={{ color: "#000000", fontWeight: "bold", fontSize: "24px" }}>
                  Role
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)" }}>
                  {Object.entries(AVAILABLE_ROLES).map(([key, value]) => {
                    return (
                      <CustomCheckBox
                        key={`${key}`}
                        label={key}
                        checked={selectedRole.map((role) => role.name).includes(value)}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          if (event.target.checked) {
                            setSelectedRole([...selectedRole, { name: value }]);
                          } else {
                            setSelectedRole(
                              selectedRole.filter((role) => role.name !== value)
                            );
                          }
                        }}
                        emitDataLayer={(checked) => {
                          generateGtmEvent({
                            event: "select",
                            action: checked ? "click" : "unclick",
                            name: "onclick",
                            type: "checkbox",
                            region: "main content",
                            section: "role",
                            text: `${key}`,
                            component: "filter",
                          });
                        }}
                      />
                    );
                  })}
                </Box>
              </>
            )}
            {currentTab === 1 && (
              <>
                <MultiSelectInputField
                  selectedItems={selectedTeacher}
                  setSelectedItems={setSelectedTeacher}
                  optionList={teacherList}
                  gtmType="select a teacher"
                  gtmSection="filter ^ teacher"
                  label={"Teacher"}
                />
              </>
            )}
            {props.currentUser.role === Role.super && currentTab !== 7 && (
              <MultiSelectInputField
                selectedItems={selectedSchool}
                setSelectedItems={setSelectedSchool}
                gtmType="select school"
                gtmSection="school"
                optionList={schoolList}
                label={"School"}
              />
            )}
            {currentTab === 0 && props.currentUser.role === Role.super && (
              <>
                <CustomCheckBox
                  checked={selectedDemo}
                  label="Demo Users"
                  onChange={(e) => {
                    setSelectedDemo(e.target.checked);
                  }}
                  emitDataLayer={(checked) => {}}
                />
              </>
            )}
            {currentTab === 1 && props.currentUser.role === Role.super && (
              <>
                <CustomCheckBox
                  checked={selectedDemo}
                  label="Demo Classes"
                  onChange={(e) => {
                    setSelectedDemo(e.target.checked);
                  }}
                  emitDataLayer={(checked) => {}}
                />
              </>
            )}
            {currentTab === 7 && (
              <>
                <MultiSelectInputField
                  selectedItems={selectedTerm}
                  setSelectedItems={setSelectedTerm}
                  gtmType="select term"
                  gtmSection="term"
                  optionList={ltiFilterOps.termOps}
                  label={"Canvas Term"}
                />
                <MultiSelectInputField
                  selectedItems={selectedCourse}
                  setSelectedItems={setSelectedCourse}
                  gtmType="select course"
                  gtmSection="course"
                  optionList={ltiFilterOps.courseOps}
                  label={"Course Name"}
                />
                <MultiSelectInputField
                  selectedItems={selectedSectionSLN}
                  setSelectedItems={setSelectedSectionSLN}
                  gtmType="select SLN"
                  gtmSection="sectionsln"
                  optionList={ltiFilterOps.slnOps}
                  label={"Section SLN"}
                />
                <MultiSelectInputField
                  selectedItems={selectedSectionNumber}
                  setSelectedItems={setSelectedSectionNumber}
                  gtmType="select section #"
                  gtmSection="section#"
                  optionList={ltiFilterOps.sectionOps}
                  label={"Canvas Section #"}
                />
              </>
            )}
          </CardContent>
          <Divider />
          <CardActions sx={{ padding: "24px 48px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}>
              <Link
                onClick={() => {
                  clearFilter();
                }}
                variant="body1">
                Clear all
              </Link>
              <Link
                onClick={() => {
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "main content",
                    section: "filter",
                    text: "show results",
                  });
                  applyFilter({
                    ...(selectedRole.length && { role: selectedRole }),
                    ...(selectedSchool.length && { school_name: selectedSchool }),
                    ...(selectedTeacher.length && { teacher_name: selectedTeacher }),
                    ...(selectedDemo.length && { is_demo: selectedDemo }),
                    ...(selectedSectionSLN.length && { section_sln: selectedSectionSLN }),
                    ...(selectedSectionNumber.length && {
                      canvas_section_number: selectedSectionNumber,
                    }),
                    ...(selectedTerm.length && { canvas_term_name: selectedTerm }),
                    ...(selectedCourse.length && { course_name: selectedCourse }),
                  });
                }}
                underline="none">
                <Button
                  size="large"
                  variant="contained"
                  sx={{
                    borderRadius: "16px",
                    width: "180px",
                    height: "62px",
                    padding: "0px",
                    "&:hover": {
                      transform: "none",
                    },
                  }}>
                  Show Results
                </Button>
              </Link>
            </Box>
          </CardActions>
        </Card>
      </Modal>
    </>
  );
};

const unique = (arr: any[]) =>
  Array.from(new Set(arr.filter((v) => v !== null && v !== undefined)));
